/**
 * Initializes the click behavior for an interactive features list.
 * On click, adds an 'active' class to the item. Clicking again removes it.
 * Only one item can be active at a time.
 *
 * Also keeps each card's `.features__hover-content` panel's hidden="until-found"
 * attribute in sync with visibility — the panel is otherwise revealed purely via CSS
 * (:hover, :has(:focus-visible), .active), so Find-in-Page can't discover its text
 * until hidden is removed to match.
 * @param {HTMLElement} listContainer - The container with [data-lazy-init="features-list"].
 */
export function initFeaturesListIndexPage(listContainer) {
	const featuresList = listContainer.querySelectorAll('.features__list-item.__hover-content');
	if (!featuresList.length) return;

	// console.log('Lazy initializing features list...', listContainer);

	const cards = Array.from(featuresList).map(item => ({
		item,
		hoverContent: item.querySelector('.features__hover-content'),
	})).filter(c => c.hoverContent);

	// Matches the CSS reveal conditions (&.active, &:hover, &:has(:focus-visible)) so
	// hidden="until-found" tracks visibility exactly.
	function isRevealed(item) {
		return item.classList.contains('active') || item.matches(':hover') || item.matches(':focus-within');
	}

	const hideTimeouts = new WeakMap();

	function syncHiddenState(item, hoverContent) {
		if (isRevealed(item)) {
			clearTimeout(hideTimeouts.get(item));
			hoverContent.removeAttribute('hidden');
			return;
		}
		// Setting hidden="until-found" synchronously here would flip content-visibility to
		// hidden instantly, cutting the CSS fade-out transition short. Deferred to the
		// transitionend listener below; this timeout is a fallback in case it never fires
		// (e.g. a very fast hover in/out that never completes a real transition).
		clearTimeout(hideTimeouts.get(item));
		hideTimeouts.set(item, setTimeout(() => {
			if (!isRevealed(item)) hoverContent.setAttribute('hidden', 'until-found');
		}, 420));
	}

	function setActive(item, hoverContent, active) {
		item.classList.toggle('active', active);
		item.setAttribute('aria-expanded', String(active));
		syncHiddenState(item, hoverContent);
	}

	function deactivateAll() {
		cards.forEach(({ item, hoverContent }) => setActive(item, hoverContent, false));
	}

	cards.forEach(({ item, hoverContent }) => {
		item.addEventListener('click', () => {
			const wasActive = item.classList.contains('active');
			deactivateAll();
			if (!wasActive) {
				setActive(item, hoverContent, true);
			}
		});

		item.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				const wasActive = item.classList.contains('active');
				deactivateAll();
				if (!wasActive) {
					setActive(item, hoverContent, true);
				}
			}
			if (e.key === 'Escape') {
				setActive(item, hoverContent, false);
				item.focus();
			}
		});

		// Mouse hover and keyboard focus reveal the panel purely via CSS, with no JS
		// involved otherwise — keep hidden in sync on every path that can reveal it.
		item.addEventListener('mouseenter', () => syncHiddenState(item, hoverContent));
		item.addEventListener('mouseleave', () => syncHiddenState(item, hoverContent));
		item.addEventListener('focusin', () => syncHiddenState(item, hoverContent));
		item.addEventListener('focusout', () => syncHiddenState(item, hoverContent));

		// Once the CSS fade-out finishes, it's safe to mark the panel hidden="until-found"
		// again without cutting the transition short.
		hoverContent.addEventListener('transitionend', (e) => {
			if (e.propertyName !== 'opacity' || isRevealed(item)) return;
			clearTimeout(hideTimeouts.get(item));
			hoverContent.setAttribute('hidden', 'until-found');
		});

		// Browser Find-in-Page reveals hidden="until-found" content on its own; make this
		// the active card so the rest of the UI stays consistent with what was revealed.
		hoverContent.addEventListener('beforematch', () => {
			deactivateAll();
			setActive(item, hoverContent, true);
		});
	});
}
